import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, MapPin, Calendar, TrendingUp, GitCompare, Info, Inbox, Plus, X, Archive } from 'lucide-react';
import permissions from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import SustainabilityBadge from '../components/common/SustainabilityBadge';
import DataSourceBadge from '../components/common/DataSourceBadge';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Batches = ({ user }) => {
  const [batches, setBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  // "active" (default) | "archived" — controls which batches are visible.
  // Archival is non-destructive; archived rows keep their full history.
  // Initial value is derived from the `?archived=true` query string so the
  // dashboard's "View archive" link deep-links directly into the archive tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibility, setVisibility] = useState(
    searchParams.get('archived') === 'true' ? 'archived' : 'active'
  );
  const navigate = useNavigate();
  const perms = permissions(user?.role);

  // Keep URL in sync with the toggle so the archive view is shareable / bookmarkable.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (visibility === 'archived') {
      next.set('archived', 'true');
    } else {
      next.delete('archived');
    }
    // Avoid pushing a duplicate history entry if nothing changed.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility]);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const query = visibility === 'archived' ? '?archived=true' : '';
      const response = await axios.get(`${BACKEND_URL}/api/batches${query}`);
      setBatches(response.data);
      setSelectedBatches([]); // reset compare selection when filter changes
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, [visibility]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const toggleBatchSelection = (batchId) => {
    setSelectedBatches(prev => {
      if (prev.includes(batchId)) {
        return prev.filter(id => id !== batchId);
      }
      if (prev.length >= 3) {
        toast.error('You can compare up to 3 batches at a time');
        return prev;
      }
      return [...prev, batchId];
    });
  };

  const handleCompare = () => {
    if (selectedBatches.length < 2) {
      toast.error('Please select at least 2 batches to compare');
      return;
    }
    navigate(`/mvp/compare?ids=${selectedBatches.join(',')}`);
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Delivered': 'bg-emerald-50 text-emerald-800 border-emerald-300',
      'In Transit': 'bg-slate-100 text-slate-700 border-slate-300',
      'Processing': 'bg-amber-50 text-amber-700 border-amber-300',
      'In Storage': 'bg-slate-100 text-slate-700 border-slate-300',
    };
    return statusMap[status] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NO', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading batches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="batches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800"
              style={{ fontFamily: 'Manrope' }}
            >
              Product Batches
            </h1>
            <DataSourceBadge
              source={batches.length > 0 ? 'live' : 'demo'}
              label={batches.length > 0 ? 'Live Data' : 'Pending Live Batch Data'}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
          <div className="text-right">
            <p className="text-2xl sm:text-3xl font-bold text-slate-800">{batches.length}</p>
            <p className="text-xs sm:text-sm text-slate-600">Total Batches</p>
          </div>
          {selectedBatches.length >= 2 && (
            <Button onClick={handleCompare} data-testid="compare-button" className="h-10">
              <GitCompare className="w-4 h-4 mr-2" />
              Compare ({selectedBatches.length})
            </Button>
          )}
          {perms.canRegisterProduct && (
            <Button
              onClick={() => setShowRegister(true)}
              data-testid="register-new-product-button"
              className="h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Register New Product
            </Button>
          )}
        </div>
      </div>

      {/* Visibility filter — Active / Archived */}
      <div
        className="flex items-center gap-2 bg-white border border-slate-200 rounded-full p-1 w-fit"
        data-testid="batch-visibility-filter"
        role="tablist"
      >
        <button
          type="button"
          onClick={() => setVisibility('active')}
          data-testid="batch-filter-active"
          aria-pressed={visibility === 'active'}
          className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${
            visibility === 'active'
              ? 'bg-emerald-700 text-white shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Active
        </button>
        <button
          type="button"
          onClick={() => setVisibility('archived')}
          data-testid="batch-filter-archived"
          aria-pressed={visibility === 'archived'}
          className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold transition-colors ${
            visibility === 'archived'
              ? 'bg-slate-900 text-white shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Archive className="w-3.5 h-3.5" />
          Archived
        </button>
      </div>

      {/* Empty state */}
      {batches.length === 0 && (
        <div
          className="bg-white rounded-xl border border-dashed border-slate-300 p-8 sm:p-12 text-center"
          data-testid="batches-empty-state"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-full mb-4">
            <Inbox className="w-7 h-7 text-slate-400" />
          </div>
          <h3
            className="text-lg sm:text-xl font-semibold text-slate-800 mb-2"
            style={{ fontFamily: 'Manrope' }}
          >
            {visibility === 'archived' ? 'No archived batches yet' : 'No batch data available'}
          </h3>
          <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
            {visibility === 'archived'
              ? 'Batches you archive from the Batch Detail page will appear here. Archived batches retain their full audit, chain-of-custody, compliance, NFC, and temperature history.'
              : 'Batch records will appear here once operational batches are registered in PureSustain. The platform is built to provide visibility across the aquaculture value chain — synthetic or placeholder values are intentionally not shown.'}
          </p>
        </div>
      )}

      {/* Batches Grid */}
      {batches.length > 0 && (
      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6"
        data-testid="batches-grid"
      >
        {batches.map((batch) => (
          <div
            key={batch.id}
            data-testid={`batch-card-${batch.id}`}
            className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 hover:shadow-lg hover:border-blue-300 transition-all duration-200 group relative"
          >
            <div className="absolute top-3 sm:top-4 left-3 sm:left-4 z-10">
              <Checkbox
                checked={selectedBatches.includes(batch.id)}
                onCheckedChange={() => toggleBatchSelection(batch.id)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`batch-checkbox-${batch.id}`}
                className="h-5 w-5"
              />
            </div>
            
            <div onClick={() => navigate(`/mvp/batches/${batch.id}`)} className="cursor-pointer">
            {/* Header */}
            <div className="flex items-start justify-between mb-3 sm:mb-4 ml-8 gap-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-mono text-slate-600 truncate">{batch.batch_number}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-800 group-hover:text-slate-900 transition-colors break-words">
                  {batch.product_name}
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 italic">{batch.species}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {batch.blockchain_verified && (
                  <div className="px-2 sm:px-3 py-0.5 sm:py-1 bg-slate-100 border border-slate-300 rounded-full text-[10px] sm:text-xs font-medium text-slate-700 whitespace-nowrap">
                    ⛓️ Verified
                  </div>
                )}
              </div>
            </div>

            {/* Status Badge */}
            <div className="mb-3 sm:mb-4">
              <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getStatusColor(batch.current_status)}`}>
                {batch.current_status}
              </span>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div>
                <div className="flex items-center gap-1.5 text-slate-600 mb-1">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs uppercase tracking-wider">Origin</span>
                </div>
                <p className="text-xs sm:text-sm font-medium text-slate-900 truncate">{batch.origin?.name || '—'}</p>
                <p className="text-[10px] sm:text-xs text-slate-500 truncate">{batch.origin?.region || ''}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-slate-600 mb-1">
                  <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs uppercase tracking-wider">Catch Date</span>
                </div>
                <p className="text-xs sm:text-sm font-medium text-slate-900">{batch.catch_date ? formatDate(batch.catch_date) : '—'}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-slate-600 mb-1">
                  <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs uppercase tracking-wider">Weight</span>
                </div>
                <p className="text-xs sm:text-sm font-medium text-slate-900">{(batch.weight_kg || 0).toLocaleString()} kg</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-slate-600 mb-1 flex-wrap">
                  <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs uppercase tracking-wider">CO₂ Total</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-0.5 px-1 py-0 bg-slate-100 text-slate-600 rounded text-[9px] sm:text-[10px] cursor-help">
                          Est
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Estimated (PoC)</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-xs sm:text-sm font-medium text-slate-900">{batch.total_co2_kg} kg</p>
              </div>
            </div>

            {/* Compliance Badges */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 pt-3 sm:pt-4 border-t border-slate-100">
              {(batch.compliance || []).slice(0, 3).map((comp, idx) => (
                <SustainabilityBadge
                  key={idx}
                  standard={comp.standard}
                  status={comp.status}
                  description={comp.description}
                />
              ))}
            </div>

            {/* Integrity Score */}
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-1 min-w-0">
                  <span className="text-[10px] sm:text-xs text-slate-600 uppercase tracking-wider truncate">Integrity Score</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-slate-400 cursor-help flex-shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-xs">
                          Integrity Score reflects data completeness, event verification, and traceability continuity within the PureSustain platform.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <span className="text-base sm:text-lg font-bold text-slate-900 whitespace-nowrap">{batch.integrity_score}/100</span>
              </div>
              <p className="text-[10px] sm:text-xs text-slate-500 mb-1.5 sm:mb-2">Based on data completeness and verification</p>
              <div className="h-1.5 sm:h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-600 rounded-full"
                  style={{ width: `${batch.integrity_score}%` }}
                ></div>
              </div>
            </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {showRegister && (
        <RegisterProductModal
          onClose={() => setShowRegister(false)}
          onCreated={(batch) => {
            setShowRegister(false);
            navigate(`/mvp/batches/${batch.id}`);
          }}
        />
      )}
    </div>
  );
};

const RegisterProductModal = ({ onClose, onCreated }) => {
  const [batchId, setBatchId] = useState('');
  const [productName, setProductName] = useState('');
  const [species, setSpecies] = useState('');
  const [supplier, setSupplier] = useState('');
  const [originName, setOriginName] = useState('');
  const [originRegion, setOriginRegion] = useState('');
  const [hatcheryName, setHatcheryName] = useState('');
  const [hatcheryDate, setHatcheryDate] = useState('');
  const [farmingSite, setFarmingSite] = useState('');
  const [farmingStartDate, setFarmingStartDate] = useState('');
  const [nfcTagId, setNfcTagId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!productName.trim()) {
      toast.error('Product name is required');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('mvp_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${BACKEND_URL}/api/batches`,
        {
          batch_id: batchId.trim() || null,
          product_name: productName.trim(),
          species: species.trim() || null,
          supplier: supplier.trim() || null,
          origin_name: originName.trim() || null,
          origin_region: originRegion.trim() || null,
          hatchery_name: hatcheryName.trim() || null,
          hatchery_date: hatcheryDate || null,
          farming_site: farmingSite.trim() || null,
          farming_start_date: farmingStartDate || null,
          nfc_tag_id: nfcTagId.trim() || null,
        },
        { headers },
      );
      // Auto-fire register_product checkpoint (writes Harvest stage event).
      try {
        await axios.post(
          `${BACKEND_URL}/api/batches/${res.data.id}/checkpoints`,
          { action: 'register_product', location: originName.trim() || null },
          { headers },
        );
      } catch { /* non-blocking */ }
      toast.success('Product registered');
      onCreated(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to register product');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60"
      onClick={onClose}
      data-testid="register-product-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-lg p-5 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Register New Product
          </h2>
          <button type="button" onClick={onClose} className="p-1 -mr-1 text-slate-500 hover:text-slate-800" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <Section title="Identity">
          <Input label="Batch ID (optional)" placeholder="e.g. SALMON-2026-001" value={batchId} onChange={setBatchId} testid="rp-batch-id" />
          <Input label="Product name" required placeholder="e.g. Atlantic Salmon" value={productName} onChange={setProductName} testid="rp-product-name" />
          <Input label="Species (optional)" placeholder="e.g. Salmo salar" value={species} onChange={setSpecies} testid="rp-species" />
          <Input label="Supplier (optional)" placeholder="e.g. PureSustain Operations" value={supplier} onChange={setSupplier} testid="rp-supplier" />
        </Section>

        <Section title="Origin">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Origin (optional)" placeholder="e.g. Lofoten, Norway" value={originName} onChange={setOriginName} testid="rp-origin" />
            <Input label="Origin region (optional)" placeholder="e.g. Nordland" value={originRegion} onChange={setOriginRegion} testid="rp-origin-region" />
          </div>
        </Section>

        <Section title="Upstream Stages">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Hatchery (optional)" placeholder="e.g. Nordland Hatchery" value={hatcheryName} onChange={setHatcheryName} testid="rp-hatchery-name" />
            <InputDate label="Hatchery date (optional)" value={hatcheryDate} onChange={setHatcheryDate} testid="rp-hatchery-date" />
            <Input label="Farming site (optional)" placeholder="e.g. Lofoten Sea Pen 3" value={farmingSite} onChange={setFarmingSite} testid="rp-farming-site" />
            <InputDate label="Farming start date (optional)" value={farmingStartDate} onChange={setFarmingStartDate} testid="rp-farming-start-date" />
          </div>
        </Section>

        <Section title="NFC Sticker (optional)">
          <Input label="NFC Tag ID" placeholder="e.g. NFC-000001" value={nfcTagId} onChange={setNfcTagId} testid="rp-nfc-tag-id" />
          <p className="text-[11px] text-slate-500 leading-snug">
            Bind a physical NFC sticker now, or leave blank and add it later from the Batch Detail page.
          </p>
        </Section>

        <p className="text-[11px] text-slate-500 leading-snug">
          Registering automatically logs a <strong>Register Product</strong> checkpoint, lighting up
          Chain of Custody, Audit Trail and the Public Verification page. Any Hatchery / Farming
          info supplied is recorded as chain-of-custody events too.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" disabled={submitting} data-testid="rp-submit" className="bg-emerald-700 hover:bg-emerald-800 text-white">
            {submitting ? 'Registering…' : 'Register Product'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{title}</p>
    <div className="space-y-3">{children}</div>
  </div>
);

const InputDate = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-[11px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
    />
  </label>
);

const Input = ({ label, value, onChange, placeholder, required, testid }) => (
  <label className="block">
    <span className="text-[11px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold">
      {label}{required && ' *'}
    </span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
    />
  </label>
);

export default Batches;
